/**
 * LivePaystubPNGPreview — Calls POST /api/paystub/preview-png with the
 * actual form data the user has entered.  The backend generates the real
 * PDF using the chosen template and returns a PNG render so we can show
 * exactly what will be downloaded.
 *
 * Lives on the final wizard step.  Re-fetches whenever templateId,
 * formData, or the active paystub index changes so changes are reflected
 * immediately.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, RefreshCw, FileText } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LivePaystubPNGPreview({ formData, templateId, previewStub, onChangeTemplate, colorMode = "original", accentHex = null }) {
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Send only the active paystub to the backend so the preview matches
  // the stub the user is looking at.  All other form fields pass through.
  const payload = useMemo(() => {
    const stubs = formData?.paystubs || [];
    const single = stubs[previewStub || 0] ? [stubs[previewStub || 0]] : stubs.slice(0, 1);
    return { ...formData, paystubs: single };
  }, [formData, previewStub]);

  // A stable cache key so we only re-fetch when payload truly changes.
  const cacheKey = useMemo(
    () => JSON.stringify({ templateId, payload, colorMode, accentHex }),
    [templateId, payload, colorMode, accentHex],
  );
  const lastKey = useRef("");

  useEffect(() => {
    if (lastKey.current === cacheKey) return;
    lastKey.current = cacheKey;
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .post(
        `${API}/api/paystub/preview-png`,
        { templateId, formData: payload, colorMode, accentHex },
        { responseType: "blob", timeout: 30000 },
      )
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setSrc((prev) => {
          if (prev) { try { URL.revokeObjectURL(prev); } catch (_) { /* noop */ } }
          return url;
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Preview failed");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cacheKey, templateId, payload]);

  return (
    <Card className="bg-zinc-900 border-zinc-700" data-testid="live-paystub-preview">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-400" />
            <div>
              <div className="text-sm font-bold text-white">Live PDF Preview</div>
              <div className="text-[11px] text-zinc-400">This is exactly what your downloaded PDF will look like.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
            <Button
              size="sm"
              variant="outline"
              onClick={onChangeTemplate}
              className="border-amber-500 text-amber-300 hover:bg-amber-500/15"
              data-testid="live-preview-change-template"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Switch Template
            </Button>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden border border-zinc-700 bg-white" style={{ minHeight: 480 }}>
          {error ? (
            <div className="p-6 text-rose-500 text-sm text-center">
              Couldn&apos;t render preview: {error}
            </div>
          ) : src ? (
            <img
              src={src}
              alt="Live paystub preview"
              className="w-full h-auto block"
              data-testid="live-paystub-preview-img"
            />
          ) : (
            <div className="p-12 text-zinc-500 text-sm text-center">
              {loading ? "Rendering your paystub…" : "Waiting for data…"}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
